import type { LendingRequest } from '@ralph/contracts'
import { formatBytes, formatCount, pluralCategory } from '@/i18n/format.ts'
import type { Messages } from '@/i18n/messages/en/index.ts'

/**
 * What the knowledge domain's refusals say, in Brazilian Portuguese (checked against the English
 * shape at compile time).
 *
 * Register is `você`, informal-professional. The ratified vocabulary of ADR-0018 is binding:
 * *base de conhecimento* (f.) — nunca *fonte*, nunca *repositório*; *documento* (m.); *agente*
 * (m.); *grupo* (m.) — nunca *time*; *administrador / administradora*; *solicitação* (f.) —
 * nunca *pedido de partilha*. *Só quem mantém* é a forma ratificada de "Only the people who
 * maintain it", e *Você gerencia* a de "You manage". Onde o inglês nomeia um administrador em
 * abstrato, aqui se diz *quem administra*: escolher *administrador* ou *administradora* para
 * alguém de quem não se sabe o gênero é adivinhar (D-21), e é o que os outros catálogos já fazem.
 *
 * ── Concordância, e onde ela decide a frase ──
 *
 * Uma *base de conhecimento* é feminina e uma *solicitação* também, então os particípios
 * concordam: "aprovada", "recusada", "retirada", "encerrada". `arquivo` (m.) é brasileiro —
 * nunca *ficheiro*. `OCR`, `MB` e `id` são termos técnicos e ficam como estão (D-15).
 */
type KnowledgeMessages = Pick<Messages['errors'], Extract<keyof Messages['errors'], `knowledge.${string}`>>

/**
 * Zero fica no singular em português — "0 documento" —, e é exatamente por isso que a escolha
 * passa por `pluralCategory` e não por uma comparação com 1 (D-19).
 */
const _documents = (count: number): string =>
  `${count} ${pluralCategory('pt-BR', count) === 'one' ? 'documento' : 'documentos'}`

/** Onde a solicitação já está. O mapa cobre o enum inteiro: um sexto status não compila sem palavra. */
const standing: Record<LendingRequest['status'], string> = {
  pending: 'aguardando resposta',
  approved: 'aprovada',
  declined: 'recusada',
  withdrawn: 'retirada',
  closed: 'encerrada'
}

/** This file's own locale — see the English sibling for why an entry names it. */
const LOCALE = 'pt-BR'

const counted = (n: number, one: string, other: string): string =>
  `${formatCount(LOCALE, n)} ${pluralCategory(LOCALE, n) === 'one' ? one : other}`

/** A cauda das duas recusas de capacidade: o que a pessoa pode de fato fazer. */
const RAISE = 'ou peça a um administrador para aumentar o limite.'

export const knowledge: KnowledgeMessages = {
  // --- a base, e quem pode vê-la ou alterá-la -------------------------------
  'knowledge.base_not_available': () => 'Nenhuma base de conhecimento com esse nome está disponível para você.',
  'knowledge.only_maintainers_can_change': () => 'Só quem mantém esta base de conhecimento pode alterá-la.',
  'knowledge.only_administrators_list_all': () => 'Só quem administra pode listar todas as bases de conhecimento.',
  'knowledge.name_taken': ({ name }) =>
    `Já existe algo chamado “${name}”. Os nomes das bases de conhecimento valem para todo o espaço de trabalho, para que as respostas possam dizer de onde vieram — escolha outro.`,
  'knowledge.nothing_to_change': () => 'Não há nada para alterar aqui.',
  'knowledge.owner_group_not_managed': () => 'Você só pode criar bases de conhecimento para grupos que você gerencia.',
  'knowledge.destination_group_not_managed': () =>
    'Você só pode mover uma base de conhecimento para um grupo que você gerencia.',
  // Sem sujeito, de propósito: esta frase recusa a mudança de dono tanto de uma base de
  // conhecimento (f.) quanto de um agente (m.), e um pronome teria de escolher um gênero
  // (D-21). O português dispensa o sujeito; o inglês diz "It".
  'knowledge.already_owned_there': () => 'Já pertence a quem você escolheu.',
  'knowledge.confirm_base_name': ({ name }) =>
    `Digite ${name} para confirmar que esta base de conhecimento deve ser excluída.`,
  'knowledge.access_changed_while_editing': () =>
    'Alguém mudou quem pode usar isto enquanto você editava. Recarregue a página e tente de novo.',
  'knowledge.ocr_language_unavailable': ({ languages, available }) =>
    `Esta instalação não tem pacote de OCR para ${languages}. Disponíveis: ${available === '' ? 'nenhum' : available}.`,

  // --- limites sobre os quais alguém pode agir ------------------------------
  'knowledge.base_at_document_limit': ({ documents }) =>
    `Esta base de conhecimento já tem ${counted(documents, 'documento', 'documentos')}, o máximo que ela comporta. ` +
    `Exclua algo que você não usa mais, ${RAISE}`,
  'knowledge.base_out_of_space': ({ limitBytes, usedBytes }) =>
    `Não há espaço suficiente nesta base de conhecimento — ela comporta até ${formatBytes(LOCALE, limitBytes)} e ` +
    `${formatBytes(LOCALE, usedBytes)} já estão em uso. Exclua um documento que você não usa mais, ou peça mais ` +
    'espaço a um administrador.',

  'knowledge.file_too_large': ({ filename, sizeBytes, limitBytes }) =>
    `“${filename}” tem ${formatBytes(LOCALE, sizeBytes)}, e um arquivo pode ter até ${formatBytes(LOCALE, limitBytes)}.`,

  // --- documentos -----------------------------------------------------------
  'knowledge.choose_a_file': () => 'Escolha um arquivo para adicionar.',
  'knowledge.document_not_in_base': () => 'Esse documento não está nesta base de conhecimento.',
  'knowledge.stored_copy_gone': () =>
    'A cópia guardada deste documento não está mais disponível. Envie o arquivo de novo.',

  // --- deixar um agente responder a partir de uma base, e pedir isso --------
  'knowledge.agent_does_not_use_base': () =>
    'Esse agente não usa esta base de conhecimento, então não há o que permitir.',
  'knowledge.base_already_usable': () => 'Você já pode usar esta base de conhecimento.',
  'knowledge.attach_base_first': () => 'Adicione a base de conhecimento ao agente antes de pedi-la.',
  'knowledge.agent_already_answers_from_base': () => 'Este agente já responde a partir dessa base de conhecimento.',
  'knowledge.ask_already_pending': () => 'Você já pediu, e ninguém respondeu ainda.',
  'knowledge.ask_declined_recently': () => 'Disseram não a isso há pouco tempo. Você pode pedir de novo amanhã.',
  'knowledge.too_many_asks': () => 'Você pediu muito conhecimento na última hora. Tente de novo mais tarde.',
  'knowledge.request_not_found': () => 'Não existe solicitação com esse id.',
  'knowledge.no_open_request_of_yours': () => 'Você não tem nenhuma solicitação aberta com esse id.',
  'knowledge.request_already_decided': ({ status }) => `Esta solicitação já está ${standing[status]}.`
}
